import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageSquare, Star, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => { setDone(false); setRating(0); setMessage(""); setName(""); }, 300);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast("Pick a bean rating first."); return; }
    if (!message.trim()) { toast("Tell us at least a word or two."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/feedback`, { rating, message, name: name || null, page: window.location.pathname });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-open-button"
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 bg-ash border border-ember/50 text-ember font-mono text-[11px] uppercase tracking-widest px-4 py-3 rounded-full hover:bg-ember hover:text-void transition-colors shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
      >
        <MessageSquare size={14} /> Feedback
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-5 bg-void/80 backdrop-blur-md"
            onClick={close}
            role="dialog"
            aria-modal="true"
            data-testid="feedback-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="feedback-modal"
              className="relative w-full max-w-md bg-ash border border-ember/40 rounded-xl p-7 md:p-8"
            >
              <button onClick={close} data-testid="feedback-close-button" className="absolute top-4 right-4 text-cream/75 hover:text-ember transition-colors">
                <X size={20} />
              </button>

              {!done ? (
                <>
                  <h3 className="font-display font-black uppercase tracking-tight text-cream text-2xl">Tell us the truth</h3>
                  <p className="font-mono text-xs text-cream/70 mt-2">Good, bad or feral. We read every one.</p>

                  <form onSubmit={submit} className="mt-6 space-y-4" data-testid="feedback-form">
                    <div className="flex items-center gap-2" data-testid="feedback-rating">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setRating(n)}
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          data-testid={`feedback-star-${n}`}
                          aria-label={`${n} of 5`}
                          className="p-1"
                        >
                          <Star
                            size={26}
                            className={(hover || rating) >= n ? "text-ember" : "text-cream/25"}
                            fill={(hover || rating) >= n ? "#E08C2D" : "none"}
                          />
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      placeholder="What's working? What's weird?"
                      data-testid="feedback-message-input"
                      className="w-full bg-void border border-white/15 rounded-lg px-4 py-3 font-mono text-sm text-cream placeholder:text-cream/30 outline-none focus:border-ember transition-colors resize-none"
                    />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name (optional)"
                      data-testid="feedback-name-input"
                      className="w-full bg-void border border-white/15 rounded-full px-5 py-3 font-mono text-sm text-cream placeholder:text-cream/30 outline-none focus:border-ember transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="feedback-submit-button"
                      className="w-full bg-ember text-void font-mono text-xs uppercase tracking-widest py-3.5 rounded-full font-medium hover:bg-emberDark transition-colors disabled:opacity-50"
                    >
                      {loading ? "Sending..." : "Send it"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-6" data-testid="feedback-success">
                  <div className="w-14 h-14 rounded-full bg-ember flex items-center justify-center mx-auto mb-5">
                    <Check size={28} className="text-void" />
                  </div>
                  <h3 className="font-display font-black uppercase tracking-tight text-cream text-2xl">Got it. Thank you.</h3>
                  <p className="font-mono text-xs text-cream/70 mt-2">The gremlins are taking notes.</p>
                  <button onClick={close} data-testid="feedback-done-button" className="mt-6 font-mono text-xs uppercase tracking-widest bg-void border border-ember/40 text-ember px-6 py-3 rounded-full hover:bg-ember hover:text-void transition-colors">
                    Back to browsing
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
